import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, QrCode, Globe, Lock, Info, X, Wand2 } from "lucide-react";
import { BoxColorSwatch } from "../components/BoxColorSwatch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { containersApi, householdsApi, formatError, voiceApi, foodApi } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { DIETARY_TAGS, todayIso, addDaysIso } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { VoiceMicButton, isSpeechRecognitionSupported } from "../components/VoiceMicButton";
import { LocationPicker, useLocations } from "../components/LocationPicker";
import { PremiumModal } from "../components/PremiumLockTile";

const AddContainer = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetQr = (searchParams.get("qr") || "").trim().toUpperCase();
  const presetBox = parseInt(searchParams.get("box") || "", 10) || null;
  const lastItemName = searchParams.get("last") || "";
  const lastItemStatus = searchParams.get("last_status") || "";
  const isReuse = Boolean(presetQr && lastItemName);
  const { user } = useAuth();
  const showVisibilityPicker = user?.default_container_visibility === "case_by_case";
  const accountDefault =
    user?.default_container_visibility === "private" ? "private" : "shared";
  const [members, setMembers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [voiceInfoOpen, setVoiceInfoOpen] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimateBasis, setEstimateBasis] = useState(null);
  // Premium upsell modal — shown when the backend rejects a new container
  // with HTTP 402 (free tier capped at 3 active). The form state is fully
  // preserved underneath, so the user can upgrade and immediately retry
  // without re-typing anything.
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [estimateInfoOpen, setEstimateInfoOpen] = useState(false);
  const lastParsedRef = React.useRef("");
  const { locations, reload: reloadLocations } = useLocations();
  const [form, setForm] = useState({
    name: "",
    notes: "",
    dietary_tags: [],
    calories_per_serving: "",
    servings: 1,
    storage_date: todayIso(),
    expiration_date: addDaysIso(4),
    owner: user?.name || "",
    visibility: accountDefault,
    location_id: null,
    box_color: "clear",
  });

  // Default the picker to the household's default location ("Fridge") once
  // locations finish loading, so the button always has a label to show.
  useEffect(() => {
    if (form.location_id) return;
    const def = locations.find((l) => l.is_default) || locations[0];
    if (def) setForm((f) => ({ ...f, location_id: def.id }));
  }, [locations, form.location_id]);

  useEffect(() => {
    householdsApi.me().then((h) => setMembers(h.members || [])).catch(() => {});
  }, []);

  const toggleTag = (t) => {
    setForm((f) => ({
      ...f,
      dietary_tags: f.dietary_tags.includes(t)
        ? f.dietary_tags.filter((x) => x !== t)
        : [...f.dietary_tags, t],
    }));
  };

  // Main mic: send transcript to backend, fill form fields with parsed result.
  // Fired both incrementally (every 1.2 s of silence) and on final stop —
  // we dedup at this layer so we never make the same LLM call twice.
  const handleVoiceForm = async (transcript, { silent = false } = {}) => {
    const text = (transcript || "").trim();
    if (!text || text === lastParsedRef.current) return;
    lastParsedRef.current = text;
    setVoiceParsing(true);
    try {
      const { parsed } = await voiceApi.parseContainer(text);
      setForm((f) => ({
        ...f,
        name: parsed.name || f.name,
        storage_date: parsed.storage_date || f.storage_date,
        expiration_date: parsed.expiration_date || f.expiration_date,
        servings: parsed.servings || f.servings,
        calories_per_serving:
          parsed.calories_per_serving != null
            ? String(parsed.calories_per_serving)
            : f.calories_per_serving,
        dietary_tags:
          parsed.dietary_tags && parsed.dietary_tags.length
            ? parsed.dietary_tags
            : f.dietary_tags,
      }));
      if (!silent) toast.success("Filled from your voice — review and tweak below");
    } catch (err) {
      // Reset dedup so the next retry (or final emit) can try again.
      lastParsedRef.current = "";
      if (!silent) toast.error(formatError(err));
    } finally {
      setVoiceParsing(false);
    }
  };

  // Notes mic: append raw transcript to the notes field, no LLM involved.
  const handleVoiceNotes = (transcript) => {
    if (!transcript) return;
    setForm((f) => ({
      ...f,
      notes: f.notes ? `${f.notes.trim()} ${transcript}` : transcript,
    }));
  };

  // AI best-by estimator — uses Claude Haiku with FDA/USDA reference data.
  const estimateBestBy = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error("Type a food name first so we know what to estimate.");
      return;
    }
    setEstimating(true);
    try {
      const res = await foodApi.estimateExpiry(name, form.storage_date);
      setForm((f) => ({ ...f, expiration_date: res.expiration_date }));
      setEstimateBasis({
        label: res.label,
        basis: res.basis,
        days: res.days,
        expiration: res.expiration_date,
      });
      toast.success(`Best-by set: ${res.days} day${res.days === 1 ? "" : "s"} (${res.label})`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setEstimating(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please give your container a name");
      return;
    }
    setSubmitting(true);
    try {
      const { box_color, ...rest } = form;
      const payload = {
        ...rest,
        calories_per_serving: rest.calories_per_serving ? parseInt(rest.calories_per_serving, 10) : null,
        servings: parseInt(rest.servings, 10) || 1,
      };
      // If the user isn't in case-by-case mode, let the backend apply their
      // account-wide default rather than locking it to whatever's in form state.
      if (!showVisibilityPicker) delete payload.visibility;
      if (presetQr) payload.qr_code = presetQr;
      const created = await containersApi.create(payload);
      // Apply the chosen box color, if any. Fire-and-forget so a color
      // miss doesn't block the user's path to the success screen — they
      // can always re-pick the color from the Boxes overlay later.
      if (box_color && box_color !== "clear" && created?.box_number) {
        containersApi.setBoxColor(created.box_number, box_color).catch(() => {});
      }
      toast.success("Container added");
      navigate(`/saved/${created.id}`);
    } catch (err) {
      // 402 = free-tier cap hit. Surface a proper Premium upsell modal
      // (the `formatError` toast on its own gave them no path forward).
      // Form state is preserved so retry-after-upgrade is one tap.
      if (err?.response?.status === 402) {
        setPremiumOpen(true);
      } else {
        toast.error(formatError(err) || "Failed to log container");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 lg:py-16">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">
          {isReuse ? "Refilling box" : "New container"}
        </div>
        <h1 className="font-heading text-4xl font-bold text-[#2D3142] mt-1">
          {isReuse ? "What's going in next?" : "What did you just store?"}
        </h1>
        {(isReuse || presetQr) && (
          <p className="text-[#4F5D75] mt-2">
            {isReuse
              ? "This Q-Box was emptied. The box number stays the same — just log the new contents."
              : "We'll attach this Q-Box sticker to whatever you're storing."}
          </p>
        )}

        {presetQr && (
          <div
            className="mt-5 flex items-start gap-3 tup-card p-4 bg-[#DCEDF0] border border-[#277A82]/30"
            data-testid="claim-sticker-banner"
          >
            <div className="w-10 h-10 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
              <QrCode className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#277A82]">
                  {isReuse ? "Reusing box" : "Claiming sticker"}
                </div>
                {presetBox && (
                  <span
                    className="text-[10px] font-bold tracking-wide bg-white text-[#1F5566] border border-[#277A82]/40 px-1.5 py-0.5 rounded"
                    data-testid="claim-box-number"
                  >
                    Box {presetBox}
                  </span>
                )}
              </div>
              <div className="font-mono text-sm text-[#1F5566] truncate mt-0.5">{presetQr}</div>
              {isReuse ? (
                <p
                  className="text-[11px] text-[#4F5D75] mt-1.5 leading-snug"
                  data-testid="reuse-last-item"
                >
                  Previously held: <strong>{lastItemName}</strong>
                  {lastItemStatus ? ` (${lastItemStatus})` : ""}.
                </p>
              ) : (
                <p className="text-[11px] text-[#4F5D75] mt-1.5 leading-snug">
                  This code names the <strong>container</strong>, not the food. You can change what's inside it anytime.
                </p>
              )}
            </div>
          </div>
        )}

        <form onSubmit={submit} data-testid="add-container-form" className="mt-8 tup-card p-7 space-y-6">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <Label htmlFor="name" className="text-[#2D3142] font-semibold">Food name *</Label>
                {isSpeechRecognitionSupported() && (
                  <div
                    className="flex items-center gap-1.5 -mb-0.5"
                    data-testid="voice-fill-controls"
                  >
                    <span className="text-[9px] font-bold tracking-[0.18em] uppercase text-[#277A82]">
                      Beta
                    </span>
                    <button
                      type="button"
                      onClick={() => setVoiceInfoOpen(true)}
                      data-testid="voice-fill-info-btn"
                      aria-label="About voice fill"
                      className="w-5 h-5 rounded-full text-[#8A93A6] hover:text-[#1F5566] flex items-center justify-center transition-colors"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>
                    <VoiceMicButton
                      onTranscript={handleVoiceForm}
                      onIncrementalTranscript={(t) => handleVoiceForm(t, { silent: true })}
                      busy={voiceParsing}
                      label="Speak"
                      size="sm"
                      testid="voice-fill-mic"
                    />
                  </div>
                )}
              </div>
              <Input
                id="name"
                data-testid="input-name"
                placeholder="e.g., Chicken & rice meal prep"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 bg-white border-[#E2E0D8] focus:border-[#277A82] focus:ring-[#277A82]/40"
              />
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <LocationPicker
                  value={form.location_id}
                  onChange={(id) => setForm((f) => ({ ...f, location_id: id }))}
                  locations={locations}
                  onLocationsChange={reloadLocations}
                  isPremium={Boolean(user?.is_premium)}
                  testId="add-location-picker"
                />
                {!user?.is_premium && (
                  <span className="text-[10px] text-[#8A93A6] tracking-wide">
                    Upgrade to add more locations
                  </span>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="storage_date" className="text-[#2D3142] font-semibold">Stored on</Label>
              <Input
                id="storage_date"
                data-testid="input-storage-date"
                type="date"
                value={form.storage_date}
                onChange={(e) => setForm({ ...form, storage_date: e.target.value })}
                className="mt-2 bg-white border-[#E2E0D8]"
              />
            </div>
            <div>
              <div className="flex items-end justify-between gap-2 flex-wrap">
                <Label htmlFor="expiration_date" className="text-[#2D3142] font-semibold">Expires on</Label>
                <div className="flex items-center gap-1.5 -mb-0.5">
                  <button
                    type="button"
                    onClick={estimateBestBy}
                    disabled={estimating}
                    data-testid="estimate-bestby-btn"
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border-2 border-[#277A82] text-[#1F5566] bg-white hover:bg-[#DCEDF0] disabled:opacity-60 transition-colors"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    {estimating ? "Thinking…" : "Estimate"}
                  </button>
                  <span className="text-[9px] font-bold tracking-[0.18em] uppercase text-[#277A82]">
                    Beta
                  </span>
                  <button
                    type="button"
                    onClick={() => setEstimateInfoOpen(true)}
                    data-testid="estimate-info-btn"
                    aria-label="About the best-by estimator"
                    className="w-5 h-5 rounded-full text-[#8A93A6] hover:text-[#1F5566] flex items-center justify-center transition-colors"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <Input
                id="expiration_date"
                data-testid="input-expiration-date"
                type="date"
                value={form.expiration_date}
                onChange={(e) => setForm({ ...form, expiration_date: e.target.value })}
                className="mt-2 bg-white border-[#E2E0D8]"
              />
              {estimateBasis && (
                <div
                  className="mt-2 text-[11px] text-[#1F5566] bg-[#DCEDF0]/50 border border-[#277A82]/25 rounded-xl px-3 py-2 leading-snug"
                  data-testid="estimate-basis-card"
                >
                  <strong>{estimateBasis.label}</strong> · {estimateBasis.days} day
                  {estimateBasis.days === 1 ? "" : "s"} — {estimateBasis.basis}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="cal" className="text-[#2D3142] font-semibold">Calories per serving</Label>
              <Input
                id="cal"
                data-testid="input-calories"
                type="number"
                min="0"
                placeholder="e.g., 420"
                value={form.calories_per_serving}
                onChange={(e) => setForm({ ...form, calories_per_serving: e.target.value })}
                className="mt-2 bg-white border-[#E2E0D8]"
              />
            </div>
            <div>
              <Label htmlFor="serv" className="text-[#2D3142] font-semibold">Servings</Label>
              <Input
                id="serv"
                data-testid="input-servings"
                type="number"
                min="1"
                value={form.servings}
                onChange={(e) => setForm({ ...form, servings: e.target.value })}
                className="mt-2 bg-white border-[#E2E0D8]"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-[#2D3142] font-semibold">Owner</Label>
              <Select value={form.owner} onValueChange={(v) => setForm({ ...form, owner: v })}>
                <SelectTrigger data-testid="select-owner" className="mt-2 bg-white border-[#E2E0D8]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {members.length === 0 && user?.name && (
                    <SelectItem value={user.name}>{user.name}</SelectItem>
                  )}
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Label htmlFor="notes" className="text-[#2D3142] font-semibold">Notes</Label>
                <VoiceMicButton
                  onTranscript={handleVoiceNotes}
                  label="Dictate notes"
                  size="sm"
                  testid="voice-notes-mic"
                />
              </div>
              <Textarea
                id="notes"
                data-testid="input-notes"
                placeholder="Anything to remember?"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 bg-white border-[#E2E0D8] min-h-[80px]"
              />
              {/* Box color — small organizational tint applied to the box
                  card in the Boxes overlay. Kept low-key here so it doesn't
                  steal attention from the primary fields. */}
              <div
                data-testid="box-color-section"
                className="mt-3 flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border border-[#E2E0D8] bg-[#FDFBF7]"
              >
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
                    Box color
                  </div>
                </div>
                <BoxColorSwatch
                  value={form.box_color}
                  onChange={(c) => setForm((f) => ({ ...f, box_color: c }))}
                  size="md"
                  testid="addcontainer-box-color"
                />
              </div>
            </div>
          </div>

          {showVisibilityPicker && (
            <div data-testid="visibility-section">
              <Label className="text-[#2D3142] font-semibold mb-3 block">Visibility</Label>
              <div className="grid grid-cols-2 gap-3" data-testid="visibility-selector">
                {[
                  { key: "shared", label: "Shared", desc: "Visible to anyone who scans (read-only outside your household)", icon: Globe },
                  { key: "private", label: "Private", desc: "Only your household can see this. Outside scanners get locked out.", icon: Lock },
                ].map((opt) => {
                  const active = form.visibility === opt.key;
                  const Icon = opt.icon;
                  return (
                    <button
                      type="button"
                      key={opt.key}
                      data-testid={`visibility-${opt.key}`}
                      onClick={() => setForm((f) => ({ ...f, visibility: opt.key }))}
                      className={`text-left rounded-2xl p-4 border-2 transition-all ${
                        active
                          ? "border-[#277A82] bg-[#DCEDF0]/40"
                          : "border-[#E2E0D8] bg-white hover:border-[#277A82]/60"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${active ? "text-[#1F5566]" : "text-[#8A93A6]"}`} />
                        <span className={`font-semibold text-sm ${active ? "text-[#1F5566]" : "text-[#2D3142]"}`}>
                          {opt.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-[#4F5D75] mt-2 leading-snug">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="text-[#2D3142] font-semibold mb-3 block">Dietary tags</Label>
            <div className="flex flex-wrap gap-2">
              {DIETARY_TAGS.map((tag) => {
                const active = form.dietary_tags.includes(tag);
                return (
                  <button
                    type="button"
                    key={tag}
                    data-testid={`tag-${tag}`}
                    onClick={() => toggleTag(tag)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? "bg-[#277A82] text-white border-[#277A82]"
                        : "bg-white text-[#4F5D75] border-[#E2E0D8] hover:border-[#277A82]"
                    }`}
                  >
                    {active && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-4 border-t border-[#E2E0D8] flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/")}
              className="border-[#E2E0D8]"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="submit-add-btn"
              className="bg-[#277A82] hover:bg-[#1F5566] text-white"
            >
              {submitting ? "Saving…" : "Save container"}
            </Button>
          </div>
        </form>
      </motion.div>

      {/* Voice-fill info modal */}
      {voiceInfoOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
          onClick={() => setVoiceInfoOpen(false)}
          data-testid="voice-info-modal-overlay"
        >
          <div
            className="w-full max-w-[440px] bg-[#FDFBF7] rounded-3xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="voice-info-modal"
          >
            <button
              onClick={() => setVoiceInfoOpen(false)}
              data-testid="voice-info-close-btn"
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#277A82]">
              Voice fill — Beta
            </div>
            <h2 className="font-heading text-xl font-bold text-[#2D3142] mt-1">
              Tap and describe what you stored
            </h2>
            <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
              Tap the <strong>Speak</strong> button, then talk naturally. The app
              listens, fills in food name, dates, servings, calories, and dietary
              tags as you speak. Pause for 3 seconds when you're done.
            </p>
            <div className="mt-4 tup-card p-4 bg-[#DCEDF0]/40 border border-[#277A82]/20">
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#277A82]">
                Try saying
              </div>
              <p className="text-sm text-[#1F5566] mt-1.5 italic leading-relaxed">
                "Leftover chicken and rice from tonight, two portions, vegan, good
                for four days."
              </p>
            </div>
            <p className="text-[11px] text-[#8A93A6] mt-3 leading-relaxed">
              Fields update in real time as you talk — review and tweak below
              before saving.
            </p>
          </div>
        </div>
      )}

      {/* Best-by estimator info modal */}
      {estimateInfoOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
          onClick={() => setEstimateInfoOpen(false)}
          data-testid="estimate-info-modal-overlay"
        >
          <div
            className="w-full max-w-[440px] bg-[#FDFBF7] rounded-3xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="estimate-info-modal"
          >
            <button
              onClick={() => setEstimateInfoOpen(false)}
              data-testid="estimate-info-close-btn"
              aria-label="Close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#277A82]">
              Best-by estimator — Beta
            </div>
            <h2 className="font-heading text-xl font-bold text-[#2D3142] mt-1">
              How this estimate is made
            </h2>
            <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
              The estimate assumes <strong>normal household refrigeration</strong>{" "}
              at ~40°F (4°C) and uses conditional logic based on the food's
              classification and likely ingredients.
            </p>
            <p className="text-sm text-[#4F5D75] mt-3 leading-relaxed">
              It draws on{" "}
              <strong>FDA refrigerated-storage guidance</strong>{" "}
              and{" "}
              <strong>USDA FoodKeeper food-safety recommendations</strong>{" "}
              — the same references USDA's own FoodKeeper app cites.
            </p>
            <div className="mt-4 tup-card p-4 bg-[#FBE9DD]/60 border border-[#E07A5F]/30">
              <p className="text-[12px] text-[#8C2A0F] leading-relaxed">
                This is an <strong>estimate, not a guarantee</strong>. If a food
                smells off, looks off, or you're unsure — when in doubt, throw it out.
                The estimator never recommends more than 14 days for cooked foods.
              </p>
            </div>
          </div>
        </div>
      )}

      <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} />
    </div>
  );
};

export default AddContainer;
